import React, { useCallback } from "react";
import classNames from "classnames";
import {
    Transaction,
    TransactionClassification as TransactionClassificationValues,
} from "@common/types/transaction";
import { TRANSACTION_CLASSIFICATION_LABELS } from "@common/constants";
import { getEntityPickerLock } from "@common/helpers/schedule-transaction-field-lock";
import { Entity } from "@common/types/entity";
import { useEntities } from "@hooks/useEntities";
import { Avatar } from "@design-system/Avatar/Avatar";
import { DEFAULT_ICON_COLOR } from "@constants";
import { TriangleIcon } from "@icons";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { TransactionEntityMenuItem } from "./TransactionEntityMenuItem";
import { useChangeTransactionEntity } from "./useChangeTransactionEntity";
import { useChangeTransactionToPersonal } from "./useChangeTransactionToPersonal";
import styles from "./TransactionEntity.module.scss";

interface Props {
    transaction: Transaction;
    isLocked?: boolean;
}

const personalLabel =
    TRANSACTION_CLASSIFICATION_LABELS[TransactionClassificationValues.PERSONAL];
const businessLabel =
    TRANSACTION_CLASSIFICATION_LABELS[TransactionClassificationValues.BUSINESS];

export const TransactionEntity: React.FC<Props> = ({
    transaction,
    isLocked,
}) => {
    const changeToBusiness = useChangeTransactionEntity(transaction);
    const changeToPersonal = useChangeTransactionToPersonal(transaction);
    const entities = useEntities({ onlyBusiness: true });
    const multiBusiness = entities.length > 1;

    const isLoading = changeToBusiness.isPending || changeToPersonal.isPending;
    const { isLocked: isEntityLocked, tooltip: lockTooltip } =
        getEntityPickerLock({
            schedule: transaction.schedule,
            isPeriodLocked: !!isLocked,
        });
    const isDisabled = isEntityLocked || isLoading;

    const handleSingleBusinessClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!transaction.isBusiness) {
                changeToBusiness.mutate(entities[0]);
            }
        },
        [changeToBusiness, entities, transaction.isBusiness],
    );

    const handlePersonalClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            changeToPersonal.mutate();
        },
        [changeToPersonal],
    );

    const handleEntitySelect = useCallback(
        (entityId: number | null) => {
            if (entityId === null) {
                return;
            }
            const entity = entities.find((e: Entity) => e.id === entityId);
            if (!entity) {
                return;
            }
            changeToBusiness.mutate(entity);
        },
        [changeToBusiness, entities],
    );

    const displayBusinessLabel = multiBusiness
        ? transaction.entity.name
        : businessLabel;

    const businessButtonContent = transaction.isBusiness ? (
        <>
            <EntityAvatar entity={transaction.entity} size="xs" aria-hidden />
            <span className={styles.label}>{displayBusinessLabel}</span>
        </>
    ) : (
        <>
            <Avatar
                name={businessLabel}
                color={DEFAULT_ICON_COLOR}
                shape="square"
                size="xs"
                aria-hidden
            />
            <span className={styles.label}>{businessLabel}</span>
        </>
    );

    const personalIcon = transaction.isBusiness ? (
        <Avatar
            name={personalLabel}
            color={DEFAULT_ICON_COLOR}
            shape="square"
            size="xs"
            aria-hidden
        />
    ) : (
        <EntityAvatar entity={transaction.entity} size="xs" aria-hidden />
    );

    const personalButtonContent = (
        <>
            {personalIcon}
            <span className={styles.label}>{personalLabel}</span>
        </>
    );

    const section = (
        <section
            data-testid="transaction-entity-section"
            className={classNames(styles.root, {
                [styles.loading]: isLoading,
            })}
        >
            {/* Business side — Select picker for multi-entity, plain button for single */}
            {multiBusiness ? (
                <Picker.Select
                    value={
                        transaction.isBusiness ? transaction.entity.id : null
                    }
                    onValueChange={handleEntitySelect}
                >
                    <Picker.Trigger
                        render={(triggerProps) => (
                            <button
                                {...triggerProps}
                                type="button"
                                disabled={isDisabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerProps.onClick?.(e);
                                }}
                                className={classNames(
                                    styles.button,
                                    styles.businessButton,
                                    {
                                        [styles.active]: transaction.isBusiness,
                                    },
                                )}
                            >
                                {businessButtonContent}
                                <TriangleIcon className={styles.arrow} />
                            </button>
                        )}
                    />
                    <Picker.Popup align="start" sideOffset={4}>
                        <Picker.List>
                            {entities.map((entity: Entity) => (
                                <TransactionEntityMenuItem
                                    key={entity.id}
                                    entity={entity}
                                />
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            ) : (
                <button
                    type="button"
                    disabled={isDisabled}
                    onClick={handleSingleBusinessClick}
                    className={classNames(
                        styles.button,
                        styles.businessButton,
                        {
                            [styles.active]: transaction.isBusiness,
                        },
                    )}
                >
                    {businessButtonContent}
                </button>
            )}

            {/* Personal button */}
            <button
                type="button"
                disabled={isDisabled}
                onClick={handlePersonalClick}
                className={classNames(styles.button, styles.personalButton, {
                    [styles.active]: !transaction.isBusiness,
                })}
            >
                {personalButtonContent}
            </button>
        </section>
    );

    return <Tooltip content={lockTooltip}>{section}</Tooltip>;
};
